import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiX, FiUser, FiMapPin, FiTruck, FiStar } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { formatTND, formatVolume, calculateTotalVolume, suggestTruckType, getTruckTypeLabel, formatMoverRating, formatDistanceKm } from './utils/helpers';

const MoverDetailsModal = ({ 
  selectedMover, 
  onClose, 
  furnitureList 
}) => {
  const navigate = useNavigate();
  const totalVolume = calculateTotalVolume(furnitureList);
  const suggestedTruck = suggestTruckType(totalVolume);

  if (!selectedMover) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="relative bg-slate-800 border border-slate-700 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        >
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-slate-600">
                  <FiUser className="w-8 h-8 text-amber-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    {selectedMover.firstName} {selectedMover.lastName}
                  </h2>
                  <div className="flex items-center gap-2 text-yellow-400 text-md mt-1">
                    <FiStar className="w-5 h-5 fill-current" />
                    <span className="font-bold">{formatMoverRating(selectedMover.rating, selectedMover.reviewCount ?? selectedMover.reviews)}</span>
                    <span className="text-gray-400 text-sm">
                      ({selectedMover.reviewCount ?? selectedMover.reviews ?? 0} avis)
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full text-gray-400 hover:bg-slate-700 transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            <div className="mt-6 pt-6 border-t border-slate-700 grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <FiMapPin className="w-5 h-5 text-amber-500" />
                <span className="text-gray-300">
                  Distance: <strong className="text-white">
                    {formatDistanceKm(selectedMover.distance)} km
                  </strong>
                </span>
              </div>
              <div className="flex items-center gap-3">
                <FiTruck className="w-5 h-5 text-amber-500" />
                <span className="text-gray-300">
                  Camion: <strong className="text-white">
                    {getTruckTypeLabel(selectedMover.truckType)}
                  </strong>
                </span>
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-gray-700">
            <h3 className="font-semibold text-white mb-3">Estimation de volume</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg">
                <span className="text-gray-300">Volume total estimé :</span>
                <span className="text-amber-400 font-semibold">
                  {formatVolume(totalVolume)}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg">
                <span className="text-gray-300">Type de camion recommandé :</span>
                <span className="text-amber-400 font-semibold">{getTruckTypeLabel(suggestedTruck)}</span>
              </div>

              {furnitureList.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                  <h4 className="text-sm font-medium text-gray-300">Meubles détectés :</h4>
                  <div className="space-y-1">
                    {furnitureList.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-gray-700/30 rounded-lg text-sm"
                      >
                        <div className="flex-1">
                          <div className="font-medium text-white">{item.name}</div>
                          <div className="text-xs text-gray-400">
                            {item.width} × {item.depth} × {item.height} cm
                            {item.quantity > 1 && ` × ${item.quantity}`}
                            {item.confidence && (
                              <span className="ml-2 px-1.5 py-0.5 bg-gray-600/50 rounded text-xs">
                                {item.confidence}% de confiance
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-amber-400 text-xs font-mono">
                          {formatVolume(
                            (item.width / 100) *
                            (item.depth / 100) *
                            (item.height / 100) *
                            (item.quantity || 1)
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-amber-300 bg-amber-900/30 p-2 rounded">
                  Aucun meuble détecté. Essayez d'ajouter des meubles manuellement ou avec une photo.
                </div>
              )}
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-700">
              <button
                onClick={() =>
                  navigate(`/client/devis?mover=${encodeURIComponent(selectedMover.id)}`, {
                    state: {
                      preselectedMover: {
                        id: selectedMover.id,
                        firstName: selectedMover.firstName,
                        lastName: selectedMover.lastName,
                        truckType: selectedMover.truckType,
                      },
                    },
                  })
                }
                className="w-full py-2.5 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 font-bold rounded-lg transition-all transform hover:scale-[1.02]"
              >
                Demander un devis
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MoverDetailsModal;





